import { useState } from "react";
import { Check } from "lucide-react";
import { appConfig } from "@/config/app";
import { pageMetadata } from "@/config/seo";
import { PlanFinder } from "@/features/marketing/sections/PlanFinder";
import { usePageMeta } from "@/features/marketing/usePageMeta";
import { Reveal } from "@/features/marketing/motion/Reveal";
import { SectionHeading } from "@/features/marketing/SectionHeading";
import { FaqList, type FaqItem } from "@/features/marketing/sections/FAQ";
import { FinalCTA } from "@/features/marketing/sections/FinalCTA";
import {
  BillingToggle,
  ENTERPRISE,
  INCLUDED,
  PricingCards,
  TIERS,
  WideTierCard,
  type BillingCycle,
} from "@/features/marketing/sections/PricingCards";
import {
  count,
  LATE_NOTICE_UNIT_PRICE,
  type Tier,
} from "@/features/marketing/sections/pricing-tiers";

const ALL_TIERS = [...TIERS, ENTERPRISE];
const ON_REQUEST = "Selon besoin";

const ROWS: Array<{ label: string; cell: (t: Tier) => string | true }> = [
  { label: "Utilisateurs", cell: (t) => (t.users === null ? ON_REQUEST : count(t.users)) },
  {
    label: "Factures actives",
    cell: (t) => (t.invoices === null ? ON_REQUEST : count(t.invoices)),
  },
  {
    label: "Mises en demeure",
    cell: (t) => (t.lateNotice === "unit" ? `${LATE_NOTICE_UNIT_PRICE} l’unité` : "Comprises"),
  },
  { label: "Toutes les fonctions", cell: () => true },
];

const PRICING_FAQ: FaqItem[] = [
  {
    q: "Faut-il une carte bancaire pour commencer\u00a0?",
    a: "Non. La création de compte est gratuite, sans carte bancaire.",
  },
  {
    q: "Mensuel ou annuel\u00a0?",
    a: "L’annuel revient environ 20 % moins cher. Sous chaque prix annuel, le total de l’année est indiqué.",
  },
  {
    q: "Et la TVA\u00a0?",
    a: "TVA non applicable, art. 293 B du CGI\u00a0: le prix affiché est celui que vous payez.",
  },
  {
    q: "Une remise pour les associations\u00a0?",
    a: "Oui\u00a0: −50 % sur Pro et Équipe pour les associations loi 1901, les mutuelles et les fondations, sur justificatif.",
  },
  {
    q: "Une mise en demeure «\u00a0à l’unité\u00a0», c’est-à-dire\u00a0?",
    a: `Sur Découverte et Essentiel, chaque mise en demeure préparée coûte ${LATE_NOTICE_UNIT_PRICE}. Sur Pro, Équipe et Sur mesure, elles sont comprises.`,
  },
];

function ComparisonTable() {
  return (
    <div role="region" aria-label="Comparaison des offres, défilement horizontal" tabIndex={0} className="relative overflow-x-auto rounded-panel border border-border bg-surface shadow-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <caption className="sr-only">Comparaison des offres</caption>
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className="w-[24%] px-5 py-4" />
            {ALL_TIERS.map((t) => (
              <th
                key={t.id}
                scope="col"
                className="px-3 py-4 text-center font-display text-base font-semibold text-foreground"
              >
                {t.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.label} className="border-b border-border last:border-b-0 hover:bg-muted/50">
              <th scope="row" className="px-5 py-3.5 text-left font-semibold text-foreground">
                {row.label}
              </th>
              {ALL_TIERS.map((t) => {
                const value = row.cell(t);
                return (
                  <td key={t.id} className="px-3 py-3.5 text-center text-muted-foreground">
                    {value === true ? (
                      <>
                        <Check aria-hidden className="mx-auto size-4 text-success" />
                        <span className="sr-only">Oui</span>
                      </>
                    ) : (
                      value
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PricingPage() {
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const meta = pageMetadata("/tarifs", appConfig.name, appConfig.domain);
  usePageMeta(meta.title, meta.description, "/tarifs");

  return (
    <>
      <section className="relative isolate overflow-hidden pt-12 pb-16 lg:pt-16">
        <div
          aria-hidden="true"
          className="bg-ruled absolute inset-x-0 top-0 -z-10 h-[520px] opacity-70"
        />
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-[44rem] text-center">
            <p className="text-xs font-semibold tracking-[0.16em] text-action uppercase">Tarifs</p>
            <h1 className="mt-4 font-display text-[clamp(2.4rem,5vw,4rem)] leading-[1.04] font-semibold tracking-[-0.02em] text-balance text-foreground">
              Vos relances, à la bonne échelle.
            </h1>
            <p className="mx-auto mt-5 max-w-[36rem] text-lg leading-relaxed text-muted-foreground">
              Choisissez selon votre équipe et vos factures actives.
              Les mêmes fonctions de relance sont incluses dans chaque offre ;
              les mises en demeure sont comprises ou facturées à l’unité.
            </p>
          </Reveal>
          <div className="mt-10 flex justify-center">
            <BillingToggle cycle={cycle} onChange={setCycle} />
          </div>
          <div className="mt-8"><PlanFinder cycle={cycle} /></div>
          <div className="mt-12">
            <PricingCards cycle={cycle} />
          </div>
          <Reveal delay={200} className="mt-5">
            <WideTierCard tier={ENTERPRISE} />
          </Reveal>
          <p className="mt-8 text-center text-caption text-subtle-foreground">
            TVA non applicable, art. 293 B du CGI. Associations loi 1901, mutuelles et
            fondations&nbsp;: −50 % sur Pro et Équipe, sur justificatif.
          </p>
        </div>
      </section>

      <section className="border-y border-border bg-surface py-20 lg:py-28">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
          <SectionHeading
            eyebrow="Dans toutes les offres"
            title="Un suivi complet, dès Découverte."
            lead="De Découverte à Sur mesure, vous avez le même outil."
          />
          <ul className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {INCLUDED.map((item, i) => (
              <Reveal as="li" key={item} delay={i * 60} className="flex gap-3 text-base text-foreground">
                <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-action-soft text-action">
                  <Check aria-hidden className="size-3.5" strokeWidth={2.5} />
                </span>
                {item}
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Comparer" title="Ce qui change d’une offre à l’autre." />
          <p className="mt-4 text-sm text-muted-foreground lg:hidden">Faites défiler le tableau horizontalement pour voir toutes les offres.</p>
          <Reveal delay={120} className="mt-6">
            <ComparisonTable />
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
          <SectionHeading eyebrow="Questions" title="Les conditions, en clair." />
          <FaqList items={PRICING_FAQ} />
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
